import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './CartPreview.css';
import { toast , ToastContainer} from "react-toastify";
import { useToastHandler } from '../../common/ToastHandler';
import config from '../../../config/Global.json';
import axios from 'axios';
import useFormattingUtils from '../../../context/useFormattingUtils';
import { createStripePaymentIntent, initializeStripe } from './stripeUtils';
import { Elements } from '@stripe/react-stripe-js';
import StripePaymentForm from './StripePaymentForm';
import TrashIcon from '../../../assets/img/trash-bin.png';
import { renderModuleIcon } from '../../../components/Pages/AppPAges/SubscriptionUI/moduleIcons';
import BackArrow from '../../../assets/SubscriptionImage/arrow (1) 1.png';
import useApi from '../../../utils/api-manager/Helper/useApi';

const PaymentComponent = ({
  inModal = false,
  onComplete,
  onBack,
  selectedModules: propModules,
  storeId: propStoreId
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleToast } = useToastHandler();
  const { formatCurrency } = useFormattingUtils();
  const { Post } = useApi();

  const fallbackData = location.state || {};
  const modules = propModules || fallbackData.selectedModules || [];
  const effectiveStoreId = propStoreId || fallbackData.storeId;

  const [moduleSelectedMonths, setModuleSelectedMonths] = useState({});
  const [selectedModules, setSelectedModules] = useState(modules);

  const [couponCode, setCouponCode] = useState('');
  const [couponId, setCouponId] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [finalAmountData, setFinalAmountData] = useState(null);
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [appliedReferralCode, setAppliedReferralCode] = useState('');
  const [referralDiscount, setReferralDiscount] = useState(0);
  const [referralAgentName, setReferralAgentName] = useState('');

  const handleBack = () => {
    inModal && onBack ? onBack() : navigate(-1);
  };

  const handleModuleMonthChange = (moduleId, months) => {
    setModuleSelectedMonths(prev => ({ ...prev, [moduleId]: months }));

    setSelectedModules(prevModules =>
      prevModules.map(module =>
        module.id === moduleId
          ? {
            ...module,
            selectedMonths: months,
            priceDetail: module.price_details.find(p => p.no_of_months === months) || module.priceDetail
          }
          : module
      )
    );

    if (couponApplied && couponCode) {
      setTimeout(() => {
        couponCodeHandler(couponCode);
      }, 100);
    }
  };

  const getModulePrice = (module) => {
    if (couponApplied && finalAmountData) {
      const featureData = finalAmountData.features?.find(f => f.feature === module.id);
      return featureData ? featureData.payable_amount : 0;
    }

    const months = moduleSelectedMonths[module.id] || module.selectedMonths || 1;
    const priceDetail = module.price_details?.find(p => p.no_of_months === months) || module.priceDetail;
    return parseFloat(priceDetail?.total_price || 0);
  };

  const formatDuration = (no_of_months, total_days) => {
    const expected = no_of_months * 30;
    const extra = total_days - expected;
    return extra > 5 ? `+ ${extra} day${extra > 1 ? 's' : ''}` : '';
  };

  const handleRemoveModule = (moduleName) => {
  const updatedModules = selectedModules.filter(module => module.name !== moduleName);
  setSelectedModules(updatedModules);

  if (couponApplied) {

    setCouponApplied(false);
    setCouponId('');
    setCouponDiscount(0);
    setFinalAmountData(null);
    setShowCouponInput(true); 
  }
};

 const couponCodeHandler = async (code, modulesList = selectedModules) => {
  try {
    const selectedModuleIds = modulesList.map(m => m.id);
    const payload = { code, selected_feature_list: selectedModuleIds };

    const validateUrl = `${config.api.host}${config.api.checkCouponValidity}`;
    const { data: couponData } = await axios.post(validateUrl, payload);

    if (!couponData?.id) {
        toast.error(couponData?.message || "Invalid coupon code");
      return false;
    }

    const finalAmountPayload = {
      coupon_id: couponData.id,
      feature_list: modulesList.map(module => ({
        feature_id: module.id,
        no_of_months: module.selectedMonths || 1,
        discount: 0.0
      })),
      store_id: effectiveStoreId
    };

    const finalAmountUrl = `${config.api.host}${config.api.getFinalAmout}`;
    const { data: finalData } = await axios.post(finalAmountUrl, finalAmountPayload);

    setCouponId(couponData.id);
    setCouponDiscount(finalData.discount_percentage.toFixed(2));
    setFinalAmountData(finalData);
 toast.success(`Coupon applied! You saved ${finalData.discount_percentage.toFixed(2)}%.`);
    return true;
  } catch (error) {
    console.error("Coupon error:", error);

    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      "Error processing coupon";

    toast.error(errorMessage);

    return false;
  }
};

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      handleToast({ type: "ERROR", message: "Please enter a coupon code" });
      return;
    }

    const isValid = await couponCodeHandler(couponCode);
    if (isValid) {
      setCouponApplied(true);
      setShowCouponInput(false);
      setCouponCode('');
    }
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(false);
    setCouponId('');
    setCouponDiscount(0);
    setFinalAmountData(null);
    setCouponCode('');
  };

  const handleApplyReferral = async () => {
    const code = referralCode.trim().toUpperCase();
    if (!code) {
      handleToast({ type: "ERROR", message: "Please enter a referral code" });
      return;
    }
    try {
      const response = await Post("validateAgentReferral", { referral_code: code, store_id: effectiveStoreId });
      const data = response.data;
      setAppliedReferralCode(data.referral_code);
      setReferralDiscount(Number(data.discount_percentage || 0));
      setReferralAgentName(data.agent_name || 'PaisaVara partner');
      setReferralCode('');
      setShowReferralInput(false);
      handleToast({ type: "SUCCESS", message: `Referral code applied — ${data.discount_percentage}% discount` });
    } catch (error) {
      console.error("Referral validation error:", error);
      handleToast({ type: "ERROR", message: error.response?.data?.detail || "Invalid referral code" });
    }
  };

  const handleRemoveReferral = () => {
    setAppliedReferralCode('');
    setReferralDiscount(0);
    setReferralAgentName('');
    setShowReferralInput(true);
    handleToast({ type: "SUCCESS", message: "Referral code removed" });
  };

  const calculateSubtotal = () =>
    selectedModules.reduce((sum, m) => sum + getModulePrice(m), 0);

  const calculateTotal = () => {
    const baseTotal = couponApplied && finalAmountData ? finalAmountData.final_total : calculateSubtotal();
    return Math.max(0, baseTotal * (1 - Number(referralDiscount || 0) / 100));
  };

  const handlePurchase = async () => {
    try {
      const totalAmount = calculateTotal();
      const paymentResponse = await createStripePaymentIntent(totalAmount);
      
      const stripe = await initializeStripe(paymentResponse.stripe_publishable_key);
      setStripePromise(stripe);
      setClientSecret(paymentResponse.client_secret);
      setShowPaymentForm(true);
    } catch (error) {
      handleToast({ type: 'ERROR', message: 'Payment initiation failed: ' + error.message });
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      const featureList = selectedModules.map(module => ({
        feature_id: module.id,
        no_of_months: module.selectedMonths || 1,
        discount: 0.0
      }));

      const payload = {
        store_id: effectiveStoreId,
        feature_list: featureList,
        overall_discount: 0.0,
        coupon_id: couponId,
        referral_code: appliedReferralCode || null
      };

      const purchaseUrl = `${config.api.host}${config.api.purchaseSubscription}`;
      await axios.post(purchaseUrl, payload);

      handleToast({ type: "SUCCESS", message: "Payment successful and features purchased" });
      onComplete?.();
      navigate('/');
    } catch (error) {
      handleToast({ type: 'ERROR', message: 'Payment confirmation failed' });
      console.error('Payment confirm error:', error);
    }
  };

  const handlePaymentError = (error) => {
    handleToast({ type: 'ERROR', message: `Payment failed: ${error}` });
    setShowPaymentForm(false);
  };

  return (
    <div className="payment-layout">
      <ToastContainer/>
      <div className="payment-back-container">
        <button className="payment-back-button" onClick={handleBack}>
          <img src={BackArrow} alt="Back" className="back-arrow-icon" />
        </button>
      </div>

      <div className="payment-main-data">
        <div className="payment-cart-preview-container">
          <div className="payment-cart-preview-section">
            <h2 className="payment-cart-title">Cart Preview</h2>

            <div className="payment-cart-table">
              <div className="cart-table-header">
                <div className="header-cell title-col">Title</div>
                <div className="header-cell duration-col">Duration</div>
                <div className="header-cell price-col">Discounted Price</div>
                <div className="header-cell discount-col">Discount %</div>
                <div className="header-cell action-col"></div>
              </div>

              <div className="payment-cart-table-body">
                {selectedModules.map(module => {
                  const currentPrice = getModulePrice(module);
                  const months = moduleSelectedMonths[module.id] || module.selectedMonths || 1;
                  const originalPriceDetail = module.price_details?.find(p => p.no_of_months === months);
                  const originalPrice = parseFloat(originalPriceDetail?.total_price || 0);
                  const hasDiscount = currentPrice < originalPrice;
                  const discountPercent = hasDiscount
                    ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100)
                    : 0;

                  return (
                    <div key={module.name} className="cart-table-row">
                      <div className="table-cell title-col">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{ transform: 'scale(1.975)' }}>
                            {renderModuleIcon(module.name)}
                          </div>
                          <span style={{ marginLeft: '30px', fontSize: '16px', color: '#000' }}>
                            {module.name}
                          </span>
                        </div>
                      </div>

                      <div className="table-cell duration-col">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <select
                            className="payment-duration-dropdown"
                            value={months}
                            onChange={(e) => handleModuleMonthChange(module.id, Number(e.target.value))}
                          >
                            {module.price_details?.map(priceDetail => (
                              <option key={priceDetail.id} value={priceDetail.no_of_months}>
                                {priceDetail.no_of_months} {priceDetail.no_of_months > 1 ? "Months" : "Month"}
                              </option>
                            ))}
                          </select>

                          {couponApplied && finalAmountData && (
                            <span style={{ marginLeft: '5px', color: '#007DC4', fontWeight: 'bold' }}>
                              {(() => {
                                const featureData = finalAmountData.features?.find(f => f.feature === module.id);
                                return featureData
                                  ? formatDuration(featureData.no_of_months, featureData.total_days)
                                  : '';
                              })()}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="table-cell price-col">
                        {formatCurrency(currentPrice.toFixed(2))}
                        {hasDiscount && (
                          <span className="original-price-strike">
                            {formatCurrency(originalPrice.toFixed(2))}
                          </span>
                        )}
                      </div>

                      <div className="table-cell discount-col">
                        {hasDiscount && (
                          <span className="discount-badge">{discountPercent}% off</span>
                        )}
                      </div>

                      <div className="table-cell action-col">
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveModule(module.name)}
                          title="Remove"
                        >
                          <img src={TrashIcon} alt="Remove" width={16} height={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="payment-coupon-section-new">
          <div className="payment-cart-summary-section">
            <h3>Order Summary</h3>

            <div className="payment-summary-row">
              <span>Subtotal:</span>
              <span>{formatCurrency(calculateSubtotal().toFixed(2))}</span>
            </div>

            <div className="payment-summary-row">
              <span>Discount:</span>
              <span>{couponDiscount}%</span>
            </div>

            {appliedReferralCode && (
              <div className="payment-summary-row" style={{ color: '#0d532f', fontWeight: '500', fontSize: '14px', marginTop: '5px' }}>
                <span>Partner Referral ({appliedReferralCode}):</span>
                <span>-{referralDiscount}% ({referralAgentName})</span>
              </div>
            )}

            <div className="payment-summary-separator" />

            <div className="payment-summary-row payment-total-row">
              <span>Total:</span>
              <span>{formatCurrency(calculateTotal().toFixed(2))}</span>
            </div>

            <button className="payment-go-to-payment-btn" onClick={handlePurchase} data-testid="go-to-payment-btn">
              Checkout
            </button>
          </div>

          {showPaymentForm && stripePromise && clientSecret && (
            <div style={{ marginTop: '20px', padding: '20px', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <h3>Payment Details</h3>
              <Elements stripe={stripePromise}>
                <StripePaymentForm
                  clientSecret={clientSecret}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  amount={calculateTotal().toFixed(2)}
                />
              </Elements>
            </div>
          )}          <div style={{ marginTop: '20px' }}>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  if (couponApplied) {
                    handleRemoveCoupon();
                    setShowCouponInput(true);
                  } else {
                    setShowCouponInput(!showCouponInput);
                  }
                }}
                style={{
                  border: "none",
                  color: couponApplied ? "#FF2024" : "#007DC4",
                  borderBottom: `2px solid ${couponApplied ? "#FF2024" : "#007DC4"}`,
                  fontWeight: "bold",
                  paddingBottom: "4px",
                  cursor: "pointer",
                  width: '140px',
                  backgroundColor: "transparent"
                }}
              >
                {couponApplied ? 'Remove Coupon' : showCouponInput ? 'Hide Coupons' : 'Apply Coupons'}
              </button>

              <button
                onClick={() => appliedReferralCode ? handleRemoveReferral() : setShowReferralInput(value => !value)}
                style={{
                  border: "none",
                  color: appliedReferralCode ? "#FF2024" : "#007DC4",
                  borderBottom: `2px solid ${appliedReferralCode ? "#FF2024" : "#007DC4"}`,
                  fontWeight: "bold",
                  paddingBottom: "4px",
                  cursor: "pointer",
                  width: '180px',
                  backgroundColor: "transparent"
                }}
              >
                {appliedReferralCode ? 'Remove Referral Code' : showReferralInput ? 'Hide Referral Code' : 'Have a Referral Code?'}
              </button>
            </div>

            {appliedReferralCode && (
              <div style={{ marginTop: '10px', color: '#0d532f', fontSize: '14px', fontWeight: 'bold' }}>
                Referral code {appliedReferralCode} applied (Partner: {referralAgentName}) — {referralDiscount}% discount
              </div>
            )}

            {(!couponApplied && showCouponInput) && (
              <div className="payment-coupon-input-section" style={{ marginTop: '15px' }}>
                <input
                  type="text"
                  placeholder="Enter Code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.trimStart())}
                  onBlur={(e) => setCouponCode(e.target.value.trim())}
                  className="payment-coupon-input-field"
                  style={{ borderBottom: '1px solid #ced4da' }}
                />
                <button
                  onClick={handleApplyCoupon}
                  className="payment-apply-coupon-button"
                  disabled={!couponCode.trim()}
                >
                  Apply Coupon
                </button>
              </div>
            )}

            {(!appliedReferralCode && showReferralInput) && (
              <div className="payment-coupon-input-section" style={{ marginTop: '15px' }}>
                <input
                  type="text"
                  placeholder="Enter agent referral code"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.trimStart().toUpperCase())}
                  className="payment-coupon-input-field"
                  style={{ borderBottom: '1px solid #ced4da' }}
                />
                <button
                  onClick={handleApplyReferral}
                  className="payment-apply-coupon-button referral-apply-button"
                  disabled={!referralCode.trim()}
                >
                  Apply Referral
                </button>
              </div>
            )}
          </div>
      </div>
    </div>
    </div>
  );
};

export default PaymentComponent;